import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Trash2,
  RefreshCw,
  Loader2,
  Download,
  Search,
  ChevronDown,
  ChevronRight,
  ToggleLeft,
  ToggleRight,
  AlertTriangle,
  AlertCircle,
  Info,
  Bug,
  Users,
  Calendar,
  Filter,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { api, getErrorMessage } from '@/services';
import dayjs from 'dayjs';
import ApiLogList, { ApiCallLoader, apiLogIdOf } from '@/components/logs/ApiLogList';

type TabType = 'all' | 'stream' | 'panel' | 'error' | 'api';

interface LogEntry {
  id: number;
  user_id: number;
  username: string;
  log_type: string;
  level: string;
  message: string;
  context: string | null;
  created_at: string;
}

interface LogStats {
  total: number;
  errors: number;
  warnings: number;
  info: number;
  last_hour: number;
  last_24h: number;
}

interface UserOption {
  id: number;
  username: string;
}

const CATEGORIES = [
  'SCHEDULE',
  'STREAM',
  'DAZN',
  'XACCEL',
  'EPG',
  'LINEAR',
  'AUTH',
  'PANEL',
  'VOD',
  'STARTED',
  'RETRY',
  'RATE-LIMIT',
  'EXPIRED',
  'ERROR',
  'DRM',
  'PLAYBACK',
  'API',
];

export default function AdminLogs() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [stats, setStats] = useState<LogStats | null>(null);
  const [users, setUsers] = useState<UserOption[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isClearing, setIsClearing] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);

  // Filters
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [levelFilter, setLevelFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [showFilters, setShowFilters] = useState(false);

  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [autoRefresh, setAutoRefresh] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Fetch users for filter
  useEffect(() => {
    api.get('/logs/admin/users').then((res) => {
      setUsers(res.data.data);
    });
  }, []);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/logs/admin/stats');
      setStats(res.data.data.stats);
    } catch {
      // Ignore
    }
  }, []);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const fetchLogs = useCallback(async () => {
    if (activeTab === 'api') return; // the API log tab loads its own data
    setIsLoading(true);
    try {
      const params: Record<string, string> = {
        page: String(page),
        limit: '100',
      };

      if (activeTab !== 'all') {
        params.log_type = activeTab;
      }
      if (selectedUser) {
        params.user_id = selectedUser;
      }
      if (levelFilter) {
        params.level = levelFilter;
      }
      if (categoryFilter) {
        params.category = categoryFilter;
      }
      if (search) {
        params.search = search;
      }
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      if (dateTo) {
        params.date_to = dateTo;
      }

      const res = await api.get('/logs/admin/search', { params });
      setLogs(res.data.data.logs);
      setTotalPages(res.data.data.pagination.pages);
      setTotalLogs(res.data.data.pagination.total);
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Error loading logs');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, page, search, selectedUser, levelFilter, categoryFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Auto-refresh
  useEffect(() => {
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => {
        fetchLogs();
        fetchStats();
      }, 5000);
    } else {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
        autoRefreshRef.current = null;
      }
    }
    return () => {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [autoRefresh, fetchLogs, fetchStats]);

  const handleSearchChange = (value: string) => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    setSearch(value);
    searchTimeoutRef.current = setTimeout(() => {
      setPage(1);
    }, 300);
  };

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setPage(1);
    setExpandedIds(new Set());
  };

  const clearFilters = () => {
    setSearch('');
    setSelectedUser('');
    setLevelFilter('');
    setCategoryFilter('');
    setDateFrom('');
    setDateTo('');
    setPage(1);
  };

  const hasActiveFilters = search || selectedUser || levelFilter || categoryFilter || dateFrom || dateTo;

  const handleClearAll = async () => {
    if (!confirm('Sei sicuro di voler eliminare TUTTI i log di TUTTI gli utenti?')) return;

    setIsClearing(true);
    try {
      await api.delete('/logs/admin/clear-all');
      toast.success('Tutti i log eliminati');
      setPage(1);
      fetchLogs();
      fetchStats();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Errore');
    } finally {
      setIsClearing(false);
    }
  };

  const handleClearUser = async (userId: number, username: string) => {
    if (!confirm(`Eliminare tutti i log di ${username}?`)) return;

    try {
      await api.delete(`/logs/admin/clear/${userId}`);
      toast.success(`Log di ${username} eliminati`);
      fetchLogs();
      fetchStats();
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Errore');
    }
  };

  const handleDownload = async () => {
    try {
      const params: Record<string, string> = {};
      if (activeTab !== 'all') params.log_type = activeTab;
      if (selectedUser) params.user_id = selectedUser;
      if (levelFilter) params.level = levelFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;

      const res = await api.get('/logs/admin/download', {
        params,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `admin_logs_${dayjs().format('YYYY-MM-DD_HH-mm')}.txt`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(getErrorMessage(error) || 'Errore download');
    }
  };

  const toggleExpanded = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getLevelIcon = (level: string) => {
    switch (level) {
      case 'error':
        return <AlertCircle size={14} className="text-error-600" />;
      case 'warn':
        return <AlertTriangle size={14} className="text-warning-600" />;
      case 'info':
        return <Info size={14} className="text-primary-600" />;
      case 'debug':
        return <Bug size={14} className="text-dark-500" />;
      default:
        return null;
    }
  };

  const getLevelBadge = (level: string) => {
    switch (level) {
      case 'error':
        return 'bg-error-50 text-error-700 border-error-200';
      case 'warn':
        return 'bg-warning-50 text-warning-700 border-warning-200';
      case 'info':
        return 'bg-primary-50 text-primary-700 border-primary-200';
      case 'debug':
        return 'bg-dark-100 text-dark-600 border-dark-300';
      default:
        return 'bg-dark-100 text-dark-500 border-dark-300';
    }
  };

  const tabs: { key: TabType; label: string }[] = [
    { key: 'all', label: 'Tutti' },
    { key: 'stream', label: 'Stream' },
    { key: 'panel', label: 'Panel' },
    { key: 'error', label: 'Errori' },
    { key: 'api', label: 'Log API' },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">Log Globali</h1>
          <p className="text-dark-400 mt-1">Visualizza i log di tutti gli utenti</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`btn-secondary ${autoRefresh ? 'ring-2 ring-success-500' : ''}`}
            title={autoRefresh ? 'Auto-refresh ON (5s)' : 'Auto-refresh OFF'}
          >
            {autoRefresh ? (
              <ToggleRight size={18} className="text-success-600" />
            ) : (
              <ToggleLeft size={18} />
            )}
          </button>
          <button onClick={() => { fetchLogs(); fetchStats(); }} disabled={isLoading} className="btn-secondary">
            {isLoading ? <Loader2 size={18} className="animate-spin" /> : <RefreshCw size={18} />}
          </button>
          <button onClick={handleDownload} className="btn-secondary">
            <Download size={18} />
            Download
          </button>
          <button onClick={handleClearAll} disabled={isClearing} className="btn-danger">
            {isClearing ? <Loader2 size={18} className="animate-spin" /> : <Trash2 size={18} />}
            Svuota Tutti
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="card p-4">
            <div className="text-2xl font-bold text-dark-100">{(stats.total ?? 0).toLocaleString()}</div>
            <div className="text-sm text-dark-500">Totale Log</div>
          </div>
          <div className="card p-4">
            <div className="text-2xl font-bold text-error-600">{(stats.errors ?? 0).toLocaleString()}</div>
            <div className="text-sm text-dark-500">Errori</div>
          </div>
          <div className="card p-4">
            <div className="text-2xl font-bold text-warning-600">{(stats.warnings ?? 0).toLocaleString()}</div>
            <div className="text-sm text-dark-500">Warning</div>
          </div>
          <div className="card p-4">
            <div className="text-2xl font-bold text-primary-600">{(stats.info ?? 0).toLocaleString()}</div>
            <div className="text-sm text-dark-500">Info</div>
          </div>
          <div className="card p-4">
            <div className="text-2xl font-bold text-success-600">{(stats.last_hour ?? 0).toLocaleString()}</div>
            <div className="text-sm text-dark-500">Ultima Ora</div>
          </div>
          <div className="card p-4">
            <div className="text-2xl font-bold text-dark-300">{(stats.last_24h ?? 0).toLocaleString()}</div>
            <div className="text-sm text-dark-500">Ultime 24h</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex items-center justify-between border-b border-dark-700">
        <div className="flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => handleTabChange(tab.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-dark-400 hover:text-dark-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`btn-secondary btn-sm mb-2 ${hasActiveFilters ? 'ring-2 ring-primary-500' : ''}`}
        >
          <Filter size={16} />
          Filtri
          {hasActiveFilters && (
            <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-500 text-white rounded-full">
              !
            </span>
          )}
        </button>
      </div>

      {activeTab === 'api' ? (
        <ApiLogList admin={true} />
      ) : (
        <>
      {/* Filters Panel */}
      {showFilters && (
        <div className="card p-4 space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-dark-200">Filtri Avanzati</h3>
            {hasActiveFilters && (
              <button onClick={clearFilters} className="text-sm text-primary-600 hover:underline flex items-center gap-1">
                <X size={14} />
                Pulisci filtri
              </button>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* User Filter */}
            <div>
              <label className="block text-sm font-medium text-dark-400 mb-1">
                <Users size={14} className="inline mr-1" />
                Utente
              </label>
              <select
                value={selectedUser}
                onChange={(e) => { setSelectedUser(e.target.value); setPage(1); }}
                className="input w-full"
              >
                <option value="">Tutti gli utenti</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.username}
                  </option>
                ))}
              </select>
            </div>

            {/* Level Filter */}
            <div>
              <label className="block text-sm font-medium text-dark-400 mb-1">Livello</label>
              <select
                value={levelFilter}
                onChange={(e) => { setLevelFilter(e.target.value); setPage(1); }}
                className="input w-full"
              >
                <option value="">Tutti i livelli</option>
                <option value="debug">Debug</option>
                <option value="info">Info</option>
                <option value="warn">Warning</option>
                <option value="error">Error</option>
              </select>
            </div>

            {/* Category Filter */}
            <div>
              <label className="block text-sm font-medium text-dark-400 mb-1">Categoria</label>
              <select
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                className="input w-full"
              >
                <option value="">Tutte le categorie</option>
                {CATEGORIES.map((cat) => (
                  <option key={cat} value={cat}>
                    [{cat}]
                  </option>
                ))}
              </select>
            </div>

            {/* Search */}
            <div>
              <label className="block text-sm font-medium text-dark-400 mb-1">
                <Search size={14} className="inline mr-1" />
                Cerca
              </label>
              <input
                type="text"
                value={search}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="Cerca nel messaggio..."
                className="input w-full"
              />
            </div>

            {/* Date From */}
            <div>
              <label className="block text-sm font-medium text-dark-400 mb-1">
                <Calendar size={14} className="inline mr-1" />
                Da
              </label>
              <input
                type="datetime-local"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="input w-full"
              />
            </div>

            {/* Date To */}
            <div>
              <label className="block text-sm font-medium text-dark-400 mb-1">
                <Calendar size={14} className="inline mr-1" />
                A
              </label>
              <input
                type="datetime-local"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="input w-full"
              />
            </div>
          </div>
        </div>
      )}

      {/* Results Count */}
      <div className="flex items-center justify-between text-sm text-dark-400">
        <span>
          {totalLogs.toLocaleString()} log trovati
          {hasActiveFilters && ' (filtrati)'}
        </span>
        {selectedUser && (
          <button
            onClick={() => handleClearUser(parseInt(selectedUser), users.find(u => u.id === parseInt(selectedUser))?.username || '')}
            className="text-error-600 hover:underline flex items-center gap-1"
          >
            <Trash2 size={14} />
            Svuota log utente selezionato
          </button>
        )}
      </div>

      {/* Logs Table */}
      {isLoading && logs.length === 0 ? (
        <div className="card">
          <div className="animate-pulse space-y-2">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="h-8 bg-dark-700 rounded" />
            ))}
          </div>
        </div>
      ) : logs.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-dark-400">Nessun log trovato</p>
        </div>
      ) : (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-dark-700 bg-dark-800">
                  <th className="text-left px-3 py-2 font-medium text-dark-400 w-8"></th>
                  <th className="text-left px-3 py-2 font-medium text-dark-400 w-40">Data/Ora</th>
                  <th className="text-left px-3 py-2 font-medium text-dark-400 w-28">Utente</th>
                  <th className="text-left px-3 py-2 font-medium text-dark-400 w-20">Livello</th>
                  <th className="text-left px-3 py-2 font-medium text-dark-400 w-20">Tipo</th>
                  <th className="text-left px-3 py-2 font-medium text-dark-400">Messaggio</th>
                </tr>
              </thead>
              <tbody className="font-mono text-xs">
                {logs.map((log) => {
                  const isExpanded = expandedIds.has(log.id);
                  const hasContext = !!log.context;

                  return (
                    <>
                      <tr
                        key={log.id}
                        className={`border-b border-dark-800 hover:bg-dark-800 cursor-pointer ${
                          isExpanded ? 'bg-dark-800' : ''
                        }`}
                        onClick={() => hasContext && toggleExpanded(log.id)}
                      >
                        <td className="px-3 py-2">
                          {hasContext ? (
                            isExpanded ? (
                              <ChevronDown size={14} className="text-dark-500" />
                            ) : (
                              <ChevronRight size={14} className="text-dark-500" />
                            )
                          ) : null}
                        </td>
                        <td className="px-3 py-2 text-dark-500 whitespace-nowrap">
                          {dayjs(log.created_at).format('YYYY-MM-DD HH:mm:ss')}
                        </td>
                        <td className="px-3 py-2">
                          <span className="px-2 py-0.5 bg-dark-700 text-dark-300 rounded text-xs">
                            {log.username}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={`inline-flex items-center gap-1 px-2 py-0.5 rounded border text-xs font-medium ${getLevelBadge(
                              log.level
                            )}`}
                          >
                            {getLevelIcon(log.level)}
                            {log.level.toUpperCase()}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-dark-500">{log.log_type}</td>
                        <td className="px-3 py-2 text-dark-200 break-all">{log.message}</td>
                      </tr>
                      {isExpanded && apiLogIdOf(log.context) && (
                        <tr key={`${log.id}-api`}>
                          <td colSpan={6} className="px-3 py-2 bg-dark-900">
                            <div className="ml-8 px-3 py-2 bg-dark-800 rounded-lg border border-dark-700">
                              <ApiCallLoader id={apiLogIdOf(log.context)!} admin />
                            </div>
                          </td>
                        </tr>
                      )}
                      {isExpanded && log.context && (
                        <tr key={`${log.id}-context`}>
                          <td colSpan={6} className="px-3 py-2 bg-dark-900">
                            <div className="ml-8 px-3 py-2 bg-dark-800 rounded-lg border border-dark-700">
                              {(() => {
                                try {
                                  const ctx = JSON.parse(log.context);
                                  if (typeof ctx !== 'object' || ctx === null) {
                                    return (
                                      <pre className="text-xs text-dark-300 whitespace-pre-wrap">
                                        {log.context}
                                      </pre>
                                    );
                                  }
                                  return (
                                    <div className="space-y-1.5 text-xs">
                                      {Object.entries(ctx).map(([key, value]) => (
                                        <div key={key} className="flex items-start gap-2">
                                          <span className="font-semibold text-dark-300 shrink-0 min-w-[100px]">
                                            {key}
                                          </span>
                                          <span className="text-dark-400">
                                            {typeof value === 'object' && value !== null ? (
                                              <pre className="font-mono text-[11px] bg-dark-900 p-2 rounded overflow-x-auto max-w-[600px]">
                                                {JSON.stringify(value, null, 2)}
                                              </pre>
                                            ) : (
                                              <span className="font-mono">{String(value)}</span>
                                            )}
                                          </span>
                                        </div>
                                      ))}
                                    </div>
                                  );
                                } catch {
                                  return (
                                    <pre className="text-xs text-dark-300 whitespace-pre-wrap">
                                      {log.context}
                                    </pre>
                                  );
                                }
                              })()}
                            </div>
                          </td>
                        </tr>
                      )}
                    </>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 p-4 border-t border-dark-700">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary btn-sm"
              >
                {t('common.previous')}
              </button>
              <span className="text-dark-400 text-sm">
                {page} / {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-secondary btn-sm"
              >
                {t('common.next')}
              </button>
            </div>
          )}
        </div>
      )}
        </>
      )}
    </div>
  );
}
